import type {
  AssetProfile,
  AssetProfileSummary,
  CalendarEvent,
  DomainSummary,
  Highlight,
  HighlightInsight,
  ProfileMarketSnapshot,
  SecondOrderDomainKey,
} from "@akem/shared";
import { getApiEndpoint } from "../config/api.js";
import {
  getDefaultProfileModuleMock,
  getProfileMock,
  getTopProfilesMock,
} from "../features/profiles/mockData.js";

export type HighlightWithInsight = Highlight & { insight?: HighlightInsight };

export type ProfileDomain =
  | "suppliers"
  | "customers"
  | "competitors"
  | "themes"
  | "news"
  | "marketTrends"
  | "politics"
  | "regulatory"
  | "mna"
  | "logistics"
  | "all";

// SecondOrderDomainKey and DomainSummary now come from @akem/shared contracts

export interface SearchResult {
  success: boolean;
  profile?: AssetProfileSummary | AssetProfile;
  needsDisambiguation?: boolean;
  candidates?: Array<{ ticker: string; name: string; confidence?: number }>;
  explanation?: string;
}

export interface ModuleDescriptor {
  id: string;
  name: string;
  isDefault?: boolean;
  profileId: string;
  widgets: Array<{
    id: string;
    type: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    config?: Record<string, any>;
    dataBinding?: { source: string; path: string };
  }>;
  layout?: any;
  connections?: Array<{
    source: { widgetId: string; outputId: string };
    target: { widgetId: string; inputId: string };
  }>;
}

export async function searchOrCreateProfile(
  query: string
): Promise<SearchResult> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/search?q=${encodeURIComponent(query)}`
  );
  if (!res.ok) throw new Error(`Profile search failed: ${res.status}`);
  return res.json();
}

export async function getProfileById(
  profileId: string
): Promise<{ success: boolean; profile: AssetProfile }> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}`
  );
  if (!res.ok) throw new Error(`Get profile failed: ${res.status}`);
  return res.json();
}

export async function getDefaultProfileModule(
  profileId: string
): Promise<{ success: boolean; module: ModuleDescriptor }> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/module`
  );
  if (!res.ok) throw new Error(`Get profile module failed: ${res.status}`);
  return res.json();
}

export async function refreshProfile(
  profileId: string,
  domains?: ProfileDomain[]
): Promise<{
  success: boolean;
  ack: boolean;
  queued?: boolean;
  local?: boolean;
}> {
  const res = await fetch(`${getApiEndpoint()}/profile/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profileId, domains }),
  });
  if (!res.ok) throw new Error(`Refresh profile failed: ${res.status}`);
  return res.json();
}

// Mock-aware helpers for prototype flows
export async function listTopProfiles(opts?: {
  useMock?: boolean;
}): Promise<AssetProfileSummary[]> {
  if (opts?.useMock !== false) return getTopProfilesMock();
  // TODO: replace when backend list endpoint exists
  return getTopProfilesMock();
}

export async function getProfile(opts: {
  idOrTicker: string;
  useMock?: boolean;
}): Promise<AssetProfile> {
  if (opts.useMock !== false) return getProfileMock(opts.idOrTicker);
  const byId = await getProfileById(opts.idOrTicker).catch(() => null);
  if (byId?.success && byId.profile) return byId.profile as AssetProfile;
  // Fallback: search
  const s = await searchOrCreateProfile(opts.idOrTicker);
  const id = (s?.profile as any)?.id || opts.idOrTicker;
  const full = await getProfileById(id).catch(() => null);
  return (full?.profile as AssetProfile) || getProfileMock(opts.idOrTicker);
}

export async function getProfileModule(opts: {
  id: string;
  useMock?: boolean;
}): Promise<ModuleDescriptor> {
  if (opts.useMock !== false) {
    const p = getProfileMock(opts.id);
    return getDefaultProfileModuleMock({
      id: p.id,
      ticker: p.ticker,
      name: p.name,
    });
  }
  const r = await getDefaultProfileModule(opts.id);
  return r.module;
}

export async function getMarketSnapshots(
  ids: string[],
  freshnessSec = 60
): Promise<Record<string, ProfileMarketSnapshot | null>> {
  const url = `${getApiEndpoint()}/profile/market/snapshots?ids=${encodeURIComponent(
    ids.join(",")
  )}&freshnessSec=${freshnessSec}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Get snapshots failed: ${res.status}`);
  const json = await res.json();
  const out: Record<string, ProfileMarketSnapshot | null> = {};
  for (const row of json.snapshots || []) {
    out[row.profileId] = row.snapshot || null;
  }
  return out;
}

export async function getProfileEvents(
  profileId: string,
  params?: { from?: string; to?: string }
): Promise<CalendarEvent[]> {
  const url = new URL(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/events`
  );
  if (params?.from) url.searchParams.set("from", params.from);
  if (params?.to) url.searchParams.set("to", params.to);
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Get profile events failed: ${res.status}`);
  const json = await res.json();
  return (json?.events as CalendarEvent[]) || [];
}

export async function getProfileHighlights(
  profileId: string
): Promise<HighlightWithInsight[]> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(
      profileId
    )}/impact/highlights`
  );
  if (!res.ok) throw new Error(`Get profile highlights failed: ${res.status}`);
  const json = await res.json();
  return (json?.highlights as HighlightWithInsight[]) || [];
}

export interface ProfileRatios {
  symbol: string;
  period: string;
  pe: number | null;
  ps: number | null;
  evToEbitda: number | null;
  roe: number | null;
  roic: number | null;
  grossMargin: number | null;
  operatingMargin: number | null;
  ebitdaMargin: number | null;
  fcfMargin: number | null;
  currentRatio: number | null;
  quickRatio: number | null;
  debtToEquity: number | null;
  updatedAt?: string;
}

export async function getProfileRatios(
  profileId: string
): Promise<ProfileRatios | null> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/ratios`
  );
  if (!res.ok) throw new Error(`Get profile ratios failed: ${res.status}`);
  const json = await res.json();
  return (json?.ratios as ProfileRatios) || null;
}

// Deprecated: getProfileFinancials removed in favor of getProfileFinancialStatements

// New comprehensive financial statements API
export async function getProfileFinancialStatements(
  profileId: string
): Promise<import("@akem/shared").FinancialStatements | null> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/financials`
  );
  if (!res.ok)
    throw new Error(`Get profile financial statements failed: ${res.status}`);
  const json = await res.json();
  return (json?.financials as any) || null;
}

export async function getProfileStats(profileId: string): Promise<{
  symbol: string;
  marketCap: number;
  currentPrice: number;
  range52w: {
    low: number;
    high: number;
  };
  sharesOutstanding?: number;
  avgVolume?: number;
  pe?: number;
  ps?: number;
  updatedAt: string;
} | null> {
  const res = await fetch(
    `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/stats`
  );
  if (!res.ok) throw new Error(`Get profile stats failed: ${res.status}`);
  const json = await res.json();
  return (json?.stats as any) || null;
}

export async function getDomainSummary(
  profileId: string,
  domain: SecondOrderDomainKey
): Promise<DomainSummary | "queued" | null> {
  const url = `${getApiEndpoint()}/profile/${encodeURIComponent(
    profileId
  )}/domains/${encodeURIComponent(domain)}/summary`;
  const res = await fetch(url);
  if (res.status === 202) return "queued";
  if (!res.ok) throw new Error(`Get domain summary failed: ${res.status}`);
  const json = await res.json();
  return (json?.summary as DomainSummary) || null;
}
